import type { ApexOptions } from 'apexcharts';
import type { ChartCard } from '@/state/widgetStore';

export type ChartSpec = {
  options: ApexOptions;
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  type: NonNullable<ApexOptions['chart']>['type'];
};

export function buildChartSpec(card: ChartCard): ChartSpec {
  switch (card.chart_type) {
    case 'pie':
    case 'donut':
      return buildCategorical(card, card.chart_type);
    case 'bar':
      return buildBar(card);
    case 'line':
    case 'scatter':
      return buildXY(card, card.chart_type);
    case 'histogram':
      return buildHistogram(card);
  }
}

function valueAt(row: Record<string, unknown>, key: string | undefined): number | null {
  if (!key) return null;
  const v = row[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function labelAt(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  return v == null || v === '' ? '(unknown)' : String(v);
}

function statsKey(card: ChartCard): string | undefined {
  if (card.stats_op) return '_v';
  return card.stats_field;
}

function buildCategorical(card: ChartCard, type: 'pie' | 'donut'): ChartSpec {
  if (!card.group_by) {
    throw new Error(`chart(${type}): group_by is required`);
  }
  const valueKey = statsKey(card);
  const categories: string[] = [];
  const values: number[] = [];
  for (const row of card.rows) {
    const label = labelAt(row, card.group_by);
    const value = valueAt(row, valueKey);
    if (value == null) continue;
    categories.push(label);
    values.push(value);
  }
  return {
    type,
    series: values,
    options: {
      labels: categories,
      title: { text: card.title, align: 'left' },
      legend: { position: 'right' },
      stroke: { width: 1 },
    },
  };
}

function buildBar(card: ChartCard): ChartSpec {
  if (!card.group_by) throw new Error('chart(bar): group_by is required');
  const valueKey = statsKey(card);
  const categories: string[] = [];
  const values: number[] = [];
  for (const row of card.rows) {
    const value = valueAt(row, valueKey);
    if (value == null) continue;
    categories.push(labelAt(row, card.group_by));
    values.push(value);
  }
  return {
    type: 'bar',
    series: [{ name: card.stats_op ?? 'value', data: values }],
    options: {
      title: { text: card.title, align: 'left' },
      xaxis: { categories },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      dataLabels: { enabled: false },
    },
  };
}

function buildXY(card: ChartCard, type: 'line' | 'scatter'): ChartSpec {
  const fields = card.out_fields ?? [];
  if (fields.length < 2) {
    throw new Error(`chart(${type}): out_fields must list at least two numeric fields (x, y).`);
  }
  const [xKey, yKey] = fields;
  const points: Array<{ x: number; y: number }> = [];
  for (const row of card.rows) {
    const x = valueAt(row, xKey);
    const y = valueAt(row, yKey);
    if (x == null || y == null) continue;
    points.push({ x, y });
  }
  return {
    type,
    series: [{ name: yKey, data: points }],
    options: {
      title: { text: card.title, align: 'left' },
      xaxis: { type: 'numeric', title: { text: xKey } },
      yaxis: { title: { text: yKey } },
      stroke: { curve: 'smooth', width: type === 'line' ? 2 : 0 },
      markers: { size: type === 'scatter' ? 5 : 0 },
    },
  };
}

function buildHistogram(card: ChartCard): ChartSpec {
  const field = card.out_fields?.[0] ?? card.stats_field;
  if (!field) throw new Error('chart(histogram): out_fields[0] or stats_field is required');
  const values: number[] = [];
  for (const row of card.rows) {
    const v = valueAt(row, field);
    if (v != null) values.push(v);
  }
  if (values.length === 0) {
    return {
      type: 'bar',
      series: [{ name: field, data: [] }],
      options: { title: { text: card.title } },
    };
  }
  values.sort((a, b) => a - b);
  const min = values[0];
  const max = values[values.length - 1];
  const buckets = Math.min(20, Math.max(5, Math.ceil(Math.sqrt(values.length))));
  const width = (max - min) / buckets || 1;
  const counts = new Array(buckets).fill(0);
  const labels: string[] = [];
  for (let i = 0; i < buckets; i++) {
    const lo = min + i * width;
    const hi = i === buckets - 1 ? max : min + (i + 1) * width;
    labels.push(`${lo.toFixed(1)}–${hi.toFixed(1)}`);
  }
  for (const v of values) {
    const idx = Math.min(buckets - 1, Math.floor((v - min) / width));
    counts[idx] += 1;
  }
  return {
    type: 'bar',
    series: [{ name: field, data: counts }],
    options: {
      title: { text: card.title, align: 'left' },
      xaxis: { categories: labels },
      plotOptions: { bar: { columnWidth: '95%' } },
      dataLabels: { enabled: false },
    },
  };
}
